import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, Trash2 } from "lucide-react";
import { COUNTRIES, CURRENCIES, regionLabelFor } from "@/lib/countries";
import { useCurrency } from "@/context/CurrencyContext";

const GOALS = ["Find new job", "Increase salary", "Become manager", "Become executive", "Switch industries", "Work remotely", "Start business", "Freelance", "Work-life balance", "Retire early", "Move country"];

const CAREER_STAGES = [
  ["", "— select —"],
  ["student", "Student"],
  ["early_career", "Early career (0–3 yrs)"],
  ["mid_career", "Mid-career (4–9 yrs)"],
  ["senior_ic", "Senior IC (10+ yrs)"],
  ["manager", "Manager"],
  ["director", "Director"],
  ["vp_executive", "VP / Executive"],
  ["career_changer", "Career changer"],
  ["returning_to_workforce", "Returning to workforce"],
  ["retired", "Retired"],
];
const EDUCATION_LEVELS = [
  ["", "— select —"], ["high_school", "High school"], ["associate", "Associate"],
  ["bachelor", "Bachelor's"], ["master", "Master's"], ["phd", "PhD"],
  ["bootcamp", "Bootcamp"], ["self_taught", "Self-taught"], ["other", "Other"],
];
const EMPLOYMENT_STATUSES = [
  ["", "— select —"],
  ["employed_full_time", "Employed — full-time"],
  ["employed_part_time", "Employed — part-time"],
  ["self_employed", "Self-employed"],
  ["contractor", "Contractor / freelance"],
  ["unemployed_searching", "Between jobs — searching"],
  ["student", "Student"],
  ["on_sabbatical", "On sabbatical"],
  ["retired", "Retired"],
];
const PRIMARY_GOALS = [
  ["", "— select —"],
  ["promotion", "Promotion at current company"],
  ["raise", "Bigger comp / raise"],
  ["new_role", "Land a new role"],
  ["career_change", "Change careers / industries"],
  ["start_business", "Start a business"],
  ["upskill", "Learn new skills"],
  ["work_life_balance", "Better work-life balance"],
  ["retire_early", "Retire early"],
];
const RISK_TOLERANCES = [
  ["", "— select —"],
  ["conservative", "Conservative — stability first"],
  ["moderate", "Moderate — balanced"],
  ["aggressive", "Aggressive — high-upside bets"],
];

export default function Profile() {
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);
  const [skillInput, setSkillInput] = useState("");
  const { currency: activeCurrency } = useCurrency();

  useEffect(() => { api.get("/profile").then(r => setP(r.data)); }, []);

  const set = (k, v) => setP({ ...p, [k]: v });
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/profile", p);
      setP(data);
      toast.success("Profile saved");
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  const toggleGoal = (g) => {
    const cur = p.career_goals || [];
    set("career_goals", cur.includes(g) ? cur.filter(x=>x!==g) : [...cur, g]);
  };

  const addSkill = () => {
    if (!skillInput.trim()) return;
    set("skills", [...(p.skills||[]), { name: skillInput.trim(), category: "Technical", level: "Intermediate" }]);
    setSkillInput("");
  };

  if (!p) return <div className="p-10">Loading...</div>;

  return (
    <div className="p-10 max-w-5xl" data-testid="profile-page">
      <div className="flex justify-between items-start mb-10">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">Your identity</div>
          <h1 className="font-display font-bold text-5xl tracking-tight">Profile</h1>
        </div>
        <button data-testid="profile-save" onClick={save} disabled={saving} className="inline-flex items-center justify-center rounded-md bg-primary text-primary-foreground hover:bg-primary/90 font-semibold transition-colors px-6 py-2.5 text-sm">{saving? "Saving..." : "Save changes"}</button>
      </div>

      <div className="space-y-8">
        {/* Personal */}
        <section className="card-flat p-6">
          <div className="font-display font-bold text-xl mb-6 tracking-tight">Personal</div>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Full name" value={p.name} onChange={v=>set("name", v)} testId="pf-name"/>
            <Field label="Current role" value={p.current_role} onChange={v=>set("current_role", v)} testId="pf-role"/>
            <Field label={`Current salary (${(p.preferred_currency || activeCurrency).toUpperCase()})`} type="number" value={p.current_salary || ""} onChange={v=>set("current_salary", v ? Number(v) : null)} testId="pf-salary"/>
            <Field label={`Target salary (${(p.preferred_currency || activeCurrency).toUpperCase()})`} type="number" value={p.target_salary || ""} onChange={v=>set("target_salary", v ? Number(v) : null)} testId="pf-target"/>
            <Field label="Years of experience" type="number" value={p.years_experience || ""} onChange={v=>set("years_experience", v ? Number(v) : null)} testId="pf-yoe"/>
            <div>
              <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Work preference</label>
              <select data-testid="pf-work-pref" value={p.work_preference || "hybrid"} onChange={e=>set("work_preference", e.target.value)} className="w-full mt-2 bg-card border border-border px-4 py-3">
                <option value="remote">Remote</option>
                <option value="hybrid">Hybrid</option>
                <option value="onsite">On-site</option>
              </select>
            </div>
          </div>
        </section>

        {/* Location (drives AI localization awareness — currency, tax context, employment law, retirement vehicles) */}
        <section className="card-flat p-6" data-testid="profile-location">
          <div className="flex items-baseline justify-between mb-2">
            <div className="font-display font-bold text-xl tracking-tight">Location</div>
            <button
              type="button"
              data-testid="pf-detect-tz"
              onClick={() => {
                try {
                  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
                  if (tz) { set("timezone", tz); toast.success(`Detected time zone: ${tz}`); }
                } catch { /* ignore */ }
              }}
              className="text-xs font-mono uppercase tracking-widest text-muted-foreground hover:text-foreground"
            >
              Detect time zone
            </button>
          </div>
          <div className="text-xs text-muted-foreground mb-6">
            Every AI response uses this — currency, tax context, retirement vehicles, employment law,
            local salary ranges. The more precise this is, the sharper your advice. Nothing here is
            shown on your public profile.
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Country</label>
              <select
                data-testid="pf-country-code"
                value={p.country_code || ""}
                onChange={e => {
                  const code = e.target.value;
                  const c = COUNTRIES.find(x => x.code === code);
                  const next = { ...p, country_code: code, country: c ? c.name : p.country };
                  // Auto-suggest currency ONLY when user has not chosen one yet.
                  if (c && !p.preferred_currency) next.preferred_currency = c.currency;
                  setP(next);
                }}
                className="w-full mt-2 bg-card border border-border px-4 py-3"
              >
                <option value="">— select country —</option>
                {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
              </select>
            </div>
            <Field
              label={regionLabelFor(p.country_code)}
              placeholder={
                p.country_code === "US" ? "e.g. California" :
                p.country_code === "CA" ? "e.g. Ontario" :
                p.country_code === "AE" ? "e.g. Dubai" :
                p.country_code === "AU" ? "e.g. New South Wales" :
                p.country_code === "IN" ? "e.g. Maharashtra" :
                "e.g. state / province / region"
              }
              value={p.region || ""}
              onChange={v => set("region", v)}
              testId="pf-region"
            />
            <Field label="City" value={p.city} onChange={v => set("city", v)} testId="pf-city" />
            <div>
              <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Preferred currency</label>
              <select
                data-testid="pf-currency"
                value={p.preferred_currency || ""}
                onChange={e => set("preferred_currency", e.target.value || null)}
                className="w-full mt-2 bg-card border border-border px-4 py-3"
              >
                <option value="">— use country default —</option>
                {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <Field
              label="Time zone (IANA)"
              placeholder="e.g. America/New_York"
              value={p.timezone || ""}
              onChange={v => set("timezone", v || null)}
              testId="pf-timezone"
            />
          </div>
        </section>

        {/* Life & Career Context (helps the AI reason about your stage in life) */}
        <section className="card-flat p-6" data-testid="profile-life-context">
          <div className="font-display font-bold text-xl mb-2 tracking-tight">Life &amp; career context</div>
          <div className="text-xs text-muted-foreground mb-6">
            The more context you share, the more precisely the AI can tailor advice, roadmaps, and salary
            projections to your life stage. All fields are optional and private — never shown on your
            public profile.
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Date of birth (used for age only)" type="date" value={p.date_of_birth || ""} onChange={v => set("date_of_birth", v || null)} testId="pf-dob" />
            <Field label="Pronouns" placeholder="she/her · he/him · they/them" value={p.pronouns} onChange={v => set("pronouns", v)} testId="pf-pronouns" />
            <SelectField label="Career stage" testId="pf-career-stage" value={p.career_stage || ""} onChange={v => set("career_stage", v || null)} options={CAREER_STAGES} />
            <Field label="Industry" placeholder="e.g. Fintech, Healthcare, EdTech" value={p.industry} onChange={v => set("industry", v)} testId="pf-industry" />
            <SelectField label="Highest education" testId="pf-education" value={p.highest_education || ""} onChange={v => set("highest_education", v || null)} options={EDUCATION_LEVELS} />
            <SelectField label="Employment status" testId="pf-employment-status" value={p.employment_status || ""} onChange={v => set("employment_status", v || null)} options={EMPLOYMENT_STATUSES} />
            <Field label="Weekly working hours" type="number" placeholder="40" value={p.weekly_work_hours || ""} onChange={v => set("weekly_work_hours", v ? Number(v) : null)} testId="pf-work-hours" />
            <Field label="Dependents (people you support)" type="number" placeholder="0" value={p.dependents_count ?? ""} onChange={v => set("dependents_count", v === "" ? null : Number(v))} testId="pf-dependents" />
            <SelectField label="Primary near-term goal" testId="pf-primary-goal" value={p.primary_career_goal || ""} onChange={v => set("primary_career_goal", v || null)} options={PRIMARY_GOALS} />
            <Field label="Time horizon (years to goal)" type="number" placeholder="3" value={p.time_horizon_years || ""} onChange={v => set("time_horizon_years", v ? Number(v) : null)} testId="pf-horizon" />
            <SelectField label="Risk tolerance" testId="pf-risk-tolerance" value={p.risk_tolerance || ""} onChange={v => set("risk_tolerance", v || null)} options={RISK_TOLERANCES} />
            <Field label="Languages you speak" placeholder="English (native), French (B2)" value={(p.languages || []).join(", ")} onChange={v => set("languages", v ? v.split(",").map(s => s.trim()).filter(Boolean) : [])} testId="pf-languages" />
          </div>
        </section>

        {/* Goals */}
        <section className="card-flat p-6" data-testid="profile-goals">
          <div className="font-display font-bold text-xl mb-6 tracking-tight">Career goals</div>
          <div className="flex flex-wrap gap-2">
            {GOALS.map(g => {
              const active = (p.career_goals||[]).includes(g);
              return (
                <button key={g} data-testid={`goal-${g.toLowerCase().replace(/[^a-z]/g,'-')}`} onClick={()=>toggleGoal(g)} className={`px-3 py-1.5 text-xs font-medium border transition-colors ${active? "bg-primary border-primary text-foreground":"bg-transparent border-border text-muted-foreground hover:border-zinc-500"}`}>{g}</button>
              );
            })}
          </div>
        </section>

        {/* Skills */}
        <section className="card-flat p-6" data-testid="profile-skills">
          <div className="font-display font-bold text-xl mb-6 tracking-tight">Skills</div>
          <div className="flex gap-2 mb-4">
            <input data-testid="skill-input" value={skillInput} onChange={e=>setSkillInput(e.target.value)} onKeyDown={e=>{if(e.key==='Enter'){e.preventDefault();addSkill();}}} placeholder="Add a skill..." className="flex-1 bg-card border border-border px-4 py-2.5" />
            <button data-testid="add-skill" onClick={addSkill} className="inline-flex items-center justify-center rounded-md border border-border bg-background hover:bg-accent text-foreground transition-colors px-4"><Plus size={14}/></button>
          </div>
          <div className="flex flex-wrap gap-2">
            {(p.skills||[]).map((s, i) => (
              <div key={i} className="flex items-center gap-2 border border-border px-3 py-1.5 text-sm bg-card">
                <span>{s.name}</span>
                <span className="text-xs text-muted-foreground font-mono">{s.level}</span>
                <button onClick={()=>set("skills", p.skills.filter((_,j)=>j!==i))} className="text-muted-foreground hover:text-red-400"><X size={12}/></button>
              </div>
            ))}
            {(!p.skills || !p.skills.length) && <div className="text-sm text-muted-foreground">No skills added. Upload your resume to auto-fill.</div>}
          </div>
        </section>

        {/* Experience preview */}
        <section className="card-flat p-6" data-testid="profile-experience">
          <div className="font-display font-bold text-xl mb-6 tracking-tight">Experience</div>
          {(p.experience||[]).length===0 ? (
            <div className="text-sm text-muted-foreground">Upload your resume to auto-import your work history.</div>
          ) : (
            <div className="space-y-3">
              {p.experience.map((e, i) => (
                <div key={i} className="border-l-2 border-primary pl-4 py-1">
                  <div className="flex items-center justify-between">
                    <div className="font-display font-bold">{e.title} <span className="text-muted-foreground font-normal">· {e.company}</span></div>
                    <button onClick={()=>set("experience", p.experience.filter((_,j)=>j!==i))} className="text-muted-foreground hover:text-red-400"><Trash2 size={12}/></button>
                  </div>
                  <div className="text-xs text-muted-foreground font-mono mt-1">{e.start_date} — {e.end_date}</div>
                  {e.achievements && <div className="text-sm text-muted-foreground mt-2">{e.achievements}</div>}
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type="text", testId, placeholder }) {
  return (
    <div>
      <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{label}</label>
      <input data-testid={testId} type={type} value={value||""} placeholder={placeholder} onChange={e=>onChange(e.target.value)} className="w-full mt-2 bg-card border border-border px-4 py-3 focus:outline-none focus:border-primary" />
    </div>
  );
}

function SelectField({ label, value, onChange, options, testId }) {
  return (
    <div>
      <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{label}</label>
      <select data-testid={testId} value={value||""} onChange={e=>onChange(e.target.value)} className="w-full mt-2 bg-card border border-border px-4 py-3 focus:outline-none focus:border-primary">
        {options.map(([val, lbl]) => <option key={val || "_none"} value={val}>{lbl}</option>)}
      </select>
    </div>
  );
}
